import { motion } from "framer-motion";
import { fadeUp, stagger } from "@/lib/motion";

const CHAPTERS = [
  {
    n: "01",
    tag: "Diagnose",
    title: "Market & Shelf Intelligence",
    desc: "We decode category velocity, price architecture and shopper behaviour before a single negotiation begins — so every mandate starts from evidence, not opinion.",
    points: ["Category & velocity audits", "Price-gap benchmarking", "Shopper mission mapping"],
  },
  {
    n: "02",
    tag: "Engineer",
    title: "Category & Velocity Engineering",
    desc: "Assortments are rebuilt around contribution per metre. We prune tail SKUs, rebalance brand tiers and design planograms that compound basket economics.",
    points: ["Assortment rationalisation", "GMROII uplift programmes", "Planogram economics"],
  },
  {
    n: "03",
    tag: "Source",
    title: "Global Supplier & Private Label Sourcing",
    desc: "From EU cellars to certified non-food factories, we open vetted supplier networks — and build private label architectures that defend margin against discounters.",
    points: ["Vetted supplier shortlists", "Private label concept-to-shelf", "Dual-source contingency"],
  },
  {
    n: "04",
    tag: "Negotiate",
    title: "High-Stakes Trade Negotiations",
    desc: "Joint business plans, rebate engineering and terms harmonisation — executed at the table alongside your team, with a 98.4% target-closure record.",
    points: ["JBP structuring", "Rebate & terms engineering", "Listing-fee negotiation"],
  },
];

export default function Manifesto() {
  return (
    <section
      id="approach"
      data-testid="manifesto-section"
      className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28"
    >
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
      >
        <motion.div variants={fadeUp} className="mb-4 flex items-center gap-4">
          <span className="h-px w-12 bg-gold" />
          <span className="font-mono text-xs font-semibold uppercase tracking-[0.28em] text-forest">
            Our Approach
          </span>
        </motion.div>
        <motion.h2
          variants={fadeUp}
          className="max-w-2xl font-serif text-2xl font-medium tracking-tight text-zinc-900 sm:text-3xl lg:text-4xl"
        >
          A doctrine, in four chapters.
        </motion.h2>
        <motion.p
          variants={fadeUp}
          className="mt-5 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg"
        >
          Every mandate moves through the same disciplined arc — diagnose,
          engineer, source, negotiate. No theatrics. Compounding margin.
        </motion.p>
      </motion.div>

      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2"
      >
        {CHAPTERS.map((c) => (
          <motion.article
            key={c.n}
            variants={fadeUp}
            data-testid={`manifesto-chapter-${c.n}`}
            className="group relative overflow-hidden border border-line bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold hover:shadow-xl lg:p-10"
          >
            <span
              aria-hidden
              className="pointer-events-none absolute -right-3 -top-7 select-none font-serif text-[120px] font-semibold leading-none text-transparent transition-colors duration-500"
              style={{ WebkitTextStroke: "1px rgba(197,160,89,0.35)" }}
            >
              {c.n}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              Chapter {c.n} — {c.tag}
            </span>
            <h3 className="mt-4 font-serif text-xl font-semibold text-zinc-900 sm:text-2xl">
              {c.title}
            </h3>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-zinc-600 sm:text-base">
              {c.desc}
            </p>
            <ul className="mt-6 space-y-2">
              {c.points.map((p) => (
                <li
                  key={p}
                  className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500"
                >
                  <span className="h-1 w-1 rotate-45 bg-forest" aria-hidden />
                  {p}
                </li>
              ))}
            </ul>
            <span
              aria-hidden
              className="absolute bottom-0 left-0 h-0.5 w-0 bg-gold transition-all duration-700 group-hover:w-full"
            />
          </motion.article>
        ))}
      </motion.div>
    </section>
  );
}
